/**
 * Local gettext shim (replaces eb-ui `@eventbrite/i18n` `gettext`/`GenericLazyString`).
 *
 * eb-ui's `@eventbrite/i18n` `gettext()` returns a lazy `GenericLazyString` object
 * that is unsafe to render directly under React 19 (and is itself an eb-ui-only
 * concept with no Valkyrie package). This shim returns a plain string (the
 * translated string if the active catalog has one, else the English source) with
 * `%(name)s` interpolation performed eagerly, so both `{gettext(...)}` and
 * `gettext(...).toString()` call sites (eb-ui source uses both) work unchanged.
 *
 * `_app` calls `setMessages(pageProps.messages)` from the request-locale catalog
 * resolved in `getInitialProps`, defaulting to `{}` (English source strings).
 */
type Interpolations = Record<string, string | number | undefined>;

let activeMessages: Record<string, string> = {};

/** Set the active translation catalog (called from `_app`). */
export const setMessages = (messages?: Record<string, string>): void => {
    activeMessages = messages || {};
};

function interpolate(translated: string, values?: Interpolations): string {
    if (!values) {
        return translated;
    }
    return translated.replace(/%\((\w+)\)s/g, (match, key: string) =>
        key in values && values[key] !== undefined ? String(values[key]) : match,
    );
}

/**
 * Interpolate `%(name)s` placeholders (the eb-ui/gettext format) with values.
 * Missing keys are left untouched (matches eb-ui's tolerant behaviour).
 *
 * ★ CONCURRENCY WARNING (Bugbot, Medium — real bug, not just theoretical):
 * `activeMessages` is a PROCESS-WIDE mutable binding, set once per request by
 * `_app`'s render pass. That's safe for calls made from WITHIN the React
 * render tree (children render strictly after `_app` calls `setMessages` for
 * THIS request, in the Pages Router's single render pass). It is NOT safe
 * for calls made from `getServerSideProps` — that function runs as a
 * SEPARATE lifecycle phase, BEFORE `_app` ever runs for this request, so it
 * always reads either an EMPTY catalog (cold start) or a STALE one (a warm
 * Lambda still holding the PREVIOUS request's messages) — never this
 * request's own. On a genuinely concurrent Node runtime it can also be
 * overwritten mid-flight by a different request's `setMessages()` call — a
 * real cross-request data leak, not just a wrong-locale bug. Any `gettext()`
 * call inside `getServerSideProps` MUST use `gettextWithCatalog()` below
 * instead, passing the SAME request-scoped catalog resolved for that
 * request (never this module-level singleton).
 */
export const gettext = (message: string, values?: Interpolations): string => {
    const translated = activeMessages[message] ?? message;
    return interpolate(translated, values);
};

/**
 * Same as `gettext()`, but takes the translation catalog EXPLICITLY rather
 * than reading the process-wide singleton — safe to call from
 * `getServerSideProps` (or any code path that runs outside the React render
 * tree), where request-scoped isolation matters. See `gettext()`'s doc
 * comment for the concurrency bug this avoids.
 */
export const gettextWithCatalog = (
    catalog: Record<string, string>,
    message: string,
    values?: Interpolations,
): string => {
    const translated = catalog[message] ?? message;
    return interpolate(translated, values);
};

/** Simple pluralization helper (eb-ui `ngettext`). Only the count decides singular/plural. */
export const ngettext = (
    singular: string,
    plural: string,
    count: number,
    values?: Interpolations,
): string => gettext(count === 1 ? singular : plural, { ...values, count });
